import React, { useState } from "react";
import { IonButton, IonContent, IonModal } from "@ionic/react";
import AnimationContainer from "./Animation";
import { ReactComponent as Crown } from "../assets/graphics/crown2.svg";
import { ReactComponent as Bookmark } from "../assets/graphics/bookmark.svg";
import { ReactComponent as Bolt } from "../assets/graphics/bolt.svg";

interface ResultButton {
  type: "clear" | "outline" | "solid";
  text: string;
  link?: string;
  action?: () => void;
}

export interface ResultModalProps {
  levelName: string;
  didPass: boolean;
  attemptsLeft: number;
  isLevelUp: boolean;
  level: number;
  unlockedFeature: string;
  buttons: ResultButton[];
  isOpen: boolean;
  onDismiss: () => void;
}

function QuoQuestResultModal(props: ResultModalProps) {
  const {
    levelName,
    didPass,
    attemptsLeft,
    isLevelUp,
    level,
    unlockedFeature,
    buttons,
    isOpen,
    onDismiss
  } = props;
  const [showParticles, setShowParticles] = useState(false);

  const attemptsLeftBgColor =
    attemptsLeft === 3 ? "bg-success" : "bg-[#fbff0a]";

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => {
        onDismiss();
        setShowParticles(false);
      }}
      onDidPresent={() => setShowParticles(true)}
      initialBreakpoint={1.0}
      breakpoints={[0, 1.0]}
    >
      <IonContent className="ion-padding text-center">
        {!isLevelUp && (
          <>
            <h2 className="text-4xl my-6 text-white font-semibold">QuoQuest</h2>
            <AnimationContainer
              animationName={
                didPass ? "botCongratsAnimation" : "botSadAnimation"
              }
            />
            <h3 className="text-4xl my-6 text-white font-semibold">
              {levelName} {didPass ? "Passed!" : "Failed"}
            </h3>
            <p className="whitespace-pre-wrap mb-6">
              {didPass
                ? "Congratulations!"
                : "Unfortunately that strategy didn't work out. Try another."}
            </p>
            {!didPass && (
              <div className="h-fit w-fit mx-auto rounded-[0.25rem] px-2 py-1 bg-primary-80 flex items-center justify-center gap-2 text-primary-30 mb-6">
                Attempts Left
                <div
                  className={`h-fit rounded-sm text-primary-80 px-1 text-sm ${attemptsLeftBgColor}`}
                >
                  {attemptsLeft}
                </div>
              </div>
            )}
            {buttons.map(btn => {
              if (btn.action !== undefined) {
                return (
                  <IonButton
                    key={btn.text}
                    fill={btn.type}
                    className="h-16 text-base text-white mb-4"
                    expand="block"
                    onClick={() => {
                      btn.action?.();
                      onDismiss();
                    }}
                  >
                    {btn.text}
                  </IonButton>
                );
              }
              return (
                <IonButton
                  key={btn.text}
                  fill={btn.type}
                  className="h-16 text-base text-white mb-4"
                  expand="block"
                  routerLink={btn.link}
                  onClick={() => onDismiss()}
                >
                  {btn.text}
                </IonButton>
              );
            })}
          </>
        )}
        {isLevelUp && (
          <LevelUp
            level={level}
            unlockedFeature={unlockedFeature}
            buttons={buttons}
            onDismiss={onDismiss}
            showParticles={showParticles}
          />
        )}
      </IonContent>
    </IonModal>
  );
}

const Particle = ({
  isLeft,
  levelBgColor,
  moveNow
}: {
  isLeft: boolean;
  levelBgColor: string;
  moveNow: boolean;
}) => {
  // You have to use the full word or tailwind won't recognise it at compile-time
  const delay = ["delay-75", "delay-10", "delay-150", "delay-200"][
    Math.floor(Math.random() * 4)
  ];
  const duration = [
    "duration-500",
    "duration-700",
    "duration-1000",
    "duration-[1500ms]"
  ][Math.floor(Math.random() * 4)];

  const xOffset = moveNow ? (5.2 + Math.random() * 2) * (isLeft ? -1 : 1) : 0;
  const yOffset = moveNow ? Math.random() * -6 : 0;
  const width = Math.random() + 0.25;
  const styleObject = {
    width: `${width}rem`,
    height: `${width}rem`,
    transform: `translate(${xOffset}rem, ${yOffset}rem)`,
    opacity: Math.random()
  };

  return (
    <div
      className={`absolute rounded-full ${levelBgColor} ${delay} ${duration}`}
      style={styleObject}
    ></div>
  );
};

const LevelUp = ({
  level,
  unlockedFeature,
  buttons,
  onDismiss,
  showParticles
}: {
  level: number;
  unlockedFeature: string;
  buttons: ResultButton[];
  onDismiss: () => void;
  showParticles: boolean;
}) => {
  const levelUnlockedName =
    level < 6
      ? "Crypto Explorer"
      : level < 11
      ? "Coin Connoisseur"
      : "Marketing Maven";
  const levelBgColor =
    level < 6 ? "bg-[#00E1FF]" : level < 11 ? "bg-[#0AF892]" : "bg-[#F4F80B]";
  const levelTextColor =
    level < 6
      ? "text-[#00E1FF]"
      : level < 11
      ? "text-[#0AF892]"
      : "text-[#F4F80B]";
  const levelImg =
    level < 6 ? (
      <Bolt className="translate-y-3" />
    ) : level < 11 ? (
      <Bookmark className="translate-y-6" />
    ) : (
      <Crown className="translate-y-5" />
    );

  return (
    // unfortunately the shadow root is a little frustrating to quickly style, so use a full-size div for laziness
    <div className="absolute top-0 left-0 h-full w-full bg-primary-80">
      {/* All the absolutely positioned divs are really only here to facilitate the gradient bg behind the image */}
      <div className="absolute top-0 left-0 w-full scale-150 aspect-square z-0 bg-[radial-gradient(circle,#6A4DFF_0%,rgba(0,0,0,0.0)_70%)]"></div>
      <div className="absolute top-0 left-0 h-full w-full flex flex-col px-4 z-10">
        <h2 className="text-4xl my-6 text-white font-semibold">QuoQuest</h2>
        <div className="h-60 w-full mx-auto relative grid place-content-center">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            {[...Array(7)].map((_, i) => (
              <Particle
                key={i}
                isLeft={level > 5}
                levelBgColor={levelBgColor}
                moveNow={showParticles}
              />
            ))}
          </div>
          <div className="absolute top-1/2 left-1/2 transform rounded-full h-1 w-1 bg-red-300"></div>
          <div className={`h-36 w-40 rounded-3xl ${levelBgColor}`}>
            {levelImg}
          </div>
        </div>
        <h3 className="text-5xl my-1 text-white font-semibold">Level Up!</h3>
        <h4 className={`text-xl font-semibold ${levelTextColor}`}>
          {levelUnlockedName}
        </h4>
        <div
          className={`mx-auto my-6 w-11 h-11 text-primary-90 rounded-lg text-3xl font-medium grid place-content-center ${levelBgColor}`}
        >
          {level}
        </div>
        <p className="whitespace-pre-wrap text-xl mb-6 px-12">
          You've unlocked the following feature!
        </p>
        <div
          className={`h-fit w-fit mx-auto mb-16 rounded-[0.25rem] px-2 py-1 bg-primary-90/50 text-primary-30 font-semibold ${levelTextColor}`}
        >
          {unlockedFeature}
        </div>
        {buttons.map(btn => {
          if (btn.action !== undefined) {
            return (
              <IonButton
                key={btn.text}
                fill={btn.type}
                className="h-16 text-base text-white mb-4"
                expand="block"
                onClick={() => {
                  btn.action?.();
                  onDismiss();
                }}
              >
                {btn.text}
              </IonButton>
            );
          }
          return (
            <IonButton
              key={btn.text}
              fill={btn.type}
              className="h-16 text-base text-white mb-4"
              expand="block"
              routerLink={btn.link}
              onClick={() => onDismiss()}
            >
              {btn.text}
            </IonButton>
          );
        })}
      </div>
    </div>
  );
};

export default QuoQuestResultModal;
